import { FormElementWithState, IpisForm } from "@eljouren/domain";
import DevConsole from "../../../DevConsole";
import {
  FileUploadHookFile,
  FileUploadNewFile,
} from "../../../hooks/file-upload-hooks";
import trpcClient from "../../../trpc-setup";
import IClientWorkOrderFileRepoV2 from "../../repos/interfaces/IClientWorkOrderFileRepoV2";
import {
  ApiClientSubmitPreparationFormOutput,
  CustomerGetPreparationFormOutput,
  DeleteFormInput,
  GetAllFormsOutput,
  GetAllValidFormConnectionsOutput,
  HandymanGetPreparationFormOutput,
  SaveFormInput,
  SaveFormOutput,
  SubmitPreparationFormInput,
  SubmitPreparationFormOutput,
} from "./preparation-form-api-client-types";
import PreparationFormFileAttacher from "./PreparationFormFileAttacher";

type ImageElementThatHasBeenAnswered =
  FormElementWithState.ValidImageGroupType & {
    state: FormElementWithState.ValidImageGroupType["state"] & {
      answer: Exclude<
        FormElementWithState.ValidImageGroupType["state"]["answer"],
        undefined
      >;
    };
  };

export default class PreparationFormApiClient {
  async saveForm(args: SaveFormInput): Promise<SaveFormOutput> {
    return trpcClient.formStaffRouter.saveForm.mutate(args);
  }

  async getAll(): Promise<GetAllFormsOutput> {
    return trpcClient.formStaffRouter.getAllForms.query();
  }

  async delete(args: DeleteFormInput): Promise<void> {
    return trpcClient.formStaffRouter.deleteForm.mutate(args);
  }

  async getAllValidFormConnections(): Promise<GetAllValidFormConnectionsOutput> {
    return trpcClient.formStaffRouter.getAllValidFormConnections.query();
  }

  async getPreparationFormForHandymanOrStaff(args: {
    fileRepo: IClientWorkOrderFileRepoV2;
    workOrderId: string;
  }): Promise<HandymanGetPreparationFormOutput> {
    const res =
      await trpcClient.authenticatedWorkOrderPreparationFormRouter.getPreparationForm.query(
        {
          workOrderId: args.workOrderId,
        }
      );
    if (!res.success) {
      throw new Error("Failed to get preparations form");
    }

    const fileAttacher = new PreparationFormFileAttacher({
      fileServerApiClient: args.fileRepo,
    });
    const transformedResult = await fileAttacher.attach(res);
    if (!transformedResult.success) {
      return transformedResult.outputWithoutFiles;
    }
    return transformedResult.output;
  }

  async getPreparationFormForCustomer(args: {
    fileRepo: IClientWorkOrderFileRepoV2;
  }): Promise<CustomerGetPreparationFormOutput> {
    const res =
      await trpcClient.customerWorkOrderPreparationFormRouter.getPreparationForm.query();
    if (!res.success) {
      return res;
    }
    const fileAttacher = new PreparationFormFileAttacher({
      fileServerApiClient: args.fileRepo,
    });

    const transformedResult = await fileAttacher.attach(res);
    if (!transformedResult.success) {
      throw transformedResult.error;
    }
    return transformedResult.output;
  }

  async submitPreparationForm(
    args: SubmitPreparationFormInput & {
      validatedForm: IpisForm.FullyValidatedType;
      fileServerApiClient: IClientWorkOrderFileRepoV2;
    }
  ): Promise<ApiClientSubmitPreparationFormOutput> {
    const res =
      await trpcClient.customerWorkOrderPreparationFormRouter.submitPreparationForm.mutate(
        {
          form: args.form,
          answers: args.answers,
        }
      );

    if (res.success) {
      const uploadResult = await this.uploadImages({
        ...args,
        output: res,
      });

      return {
        serverSideResult: res,
        imageUploadResult: uploadResult,
      };
    }

    return {
      serverSideResult: res,
    };
  }

  /* 
    The name of these images will be previously set to match the prompt of the question in
    ChecklistImageUploadComponent.tsx.

    This is how we link the image to the question, which is very hacky. More info in the comment
    in ChecklistImageUploadComponent.tsx.
  */
  private async uploadImages(
    args: SubmitPreparationFormInput & {
      validatedForm: IpisForm.FullyValidatedType;
      fileServerApiClient: IClientWorkOrderFileRepoV2;
      output: Extract<SubmitPreparationFormOutput, { success: true }>;
    }
  ): Promise<"success" | "partial-success" | "failed"> {
    const getNewImages = (el: ImageElementThatHasBeenAnswered) => {
      const imageMap = el.state.answer.images;

      const values: FileUploadHookFile[] = Object.values(imageMap)
        .filter((v): v is FileUploadHookFile[] => !!v && Array.isArray(v))
        .flat();
      const newValues = values.filter(
        (v): v is FileUploadNewFile => v.state === "preupload"
      );
      DevConsole.log(`New values count: ${newValues.length}`);
      return newValues;
    };

    const presignedObjects = args.output.presignedUploadUrls;
    DevConsole.log(
      `Presigned objects count: ${Object.keys(presignedObjects).length}`
    );

    const allElements = args.validatedForm.pages.flatMap((p) => p.elements);
    const imageElementsWithAnswers = allElements.filter(
      (el): el is ImageElementThatHasBeenAnswered =>
        el.typeOfQuestion === "image-group" &&
        el.state.answer !== undefined &&
        Object.values(el.state.answer.images).length > 0
    );

    const totalImageCount = imageElementsWithAnswers.reduce(
      (acc, curr) => {
        return acc + getNewImages(curr).length;
      },

      0
    );

    const elementsWithoutAPresignedUrl: ImageElementThatHasBeenAnswered[] = [];
    const elementsThatFailedToUpload: ImageElementThatHasBeenAnswered[] = [];
    let failedCount = 0;
    let successCount = 0;
    const errors: PromiseRejectedResult["reason"][] = [];

    const elementsWithPresignedUrlAndPositiveLengthImages =
      imageElementsWithAnswers.filter((el) => {
        const url = presignedObjects[el.id]?.presignResponse.url;
        if (!url) {
          DevConsole.log(`Missing presigned url for ${el.id}`);
          elementsWithoutAPresignedUrl.push(el);
          failedCount += getNewImages(el).length;
          return false;
        }

        return true;
      });

    const promises = elementsWithPresignedUrlAndPositiveLengthImages.map(
      (el) => {
        const url = presignedObjects[el.id].presignResponse.url;
        const newImages = getNewImages(el);
        return args.fileServerApiClient.uploadWithPresignedUrl({
          url,
          files: newImages,
        });
      }
    );

    const results = await Promise.allSettled(promises);
    results.forEach((result, index) => {
      if (result.status === "fulfilled") {
        const response = result.value;
        failedCount +=
          response.failedFiles.length + response.rejectedFiles.length;
        successCount += response.uploadedFiles.length;
      } else {
        errors.push(result.reason);
        const element = imageElementsWithAnswers[index];
        elementsThatFailedToUpload.push(element);
        failedCount += getNewImages(element).length;
      }
    });

    DevConsole.log(`Success count: ${successCount} / ${totalImageCount}`);
    DevConsole.log(`Failed count: ${failedCount} / ${totalImageCount}`);

    const allSuccess = successCount === totalImageCount;
    const someSuccess = successCount > 0;
    if (allSuccess) {
      return "success";
    } else if (someSuccess) {
      return "partial-success";
    }

    return "failed";
  }

  async resetFormAnswers(): Promise<void> {
    if (process.env.NODE_ENV !== "development") {
      throw new Error("This method is only available in development mode");
    }
    await trpcClient.formCustomerRouter.resetAnswers.mutate();
  }
}
